import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useCRMHost } from '../CRMHostContext';

import { CRM_ACTION } from '../contracts';
import { mergeAccountIntoMember, toAuditEntriesByClaim, toUiClaim as crmToUi, rejectReasonText } from '../adapters';
import { ClaimQueueScreen } from '../../screens/ClaimQueueScreen';
import type { Claim as UiClaim } from '../../screens/mockData';
import type { AuditEntry, QueueStatus } from '../../components';

/** จำนวนคำขอที่ดึงมาเก็บไว้ในเครื่องต่อหนึ่งรอบ */
const PAGE_LIMIT = 500;

/**
 * ตัวต่อระหว่างหน้าคิวตรวจคำขอกับชั้นข้อมูล
 *
 * มีสองแหล่ง สลับด้วย env ตัวเดียว หน้าจอไม่รู้ว่าใช้แหล่งไหน
 *   useMockData = true   ข้อมูลจำลอง 1,240 รายการในเครื่อง
 *   useMockData = false  CRM module ของ HIS จริง (/apis/CRM/point-claim/)
 *
 * ✅ เดิมต้องดึง `loyalty-account` ทั้ง division มา join เอง เพราะคำขอส่ง `account`
 *    มาเป็นเลข id ตอนนี้ backend ฝัง object พร้อมชื่อจริงมาให้แล้ว จึงเหลือคำขอชุดเดียว
 */
/** เหตุผลที่ส่งเมื่อกด "เลิกทำ" ทันทีหลังตัดสิน */
const UNDO_COMMENT = 'เลิกทำทันทีหลังตัดสิน (กดผิด)';

export function ClaimQueueContainer() {
  const { api, dataMode, mock, refreshKey } = useCRMHost();
  const isMock = dataMode === 'mock';

  const [claims, setClaims] = useState<UiClaim[] | undefined>(undefined);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  /**
   * อัตราแลกเปลี่ยนคะแนนจาก earning policy ของ division
   *
   * ⚠️ ห้ามใช้ค่าคงที่ในโค้ด — อัตราตั้งได้ต่อ division และตอนนี้ division 545
   *    ตั้งไว้ที่ 1 บาท = 1 คะแนน ไม่ใช่ 100 บาท = 1 คะแนนตาม SRS
   *    ถ้าใช้ค่าคงที่ ตัวเลขที่เจ้าหน้าที่เห็นจะผิด 100 เท่า
   */
  const [rewardMultiplier, setRewardMultiplier] = useState<number | null>(null);

  /**
   * เงื่อนไขที่ส่งไปกรองฝั่ง server — หน้าจอเป็นคนบอกมา
   *
   * เก็บเป็น state แยกเพื่อให้ `load` ถูกสร้างใหม่เมื่อเงื่อนไขเปลี่ยน
   * แล้ว useEffect ยิงซ้ำให้เอง ไม่ต้องเรียก load เองจากหลายที่
   */
  const [serverFilter, setServerFilter] = useState<{
    name?: string; amountGte?: number; amountLte?: number;
  }>({});

  /** ประวัติการตรวจของจริง จัดกลุ่มตามคำขอ — undefined = ยังไม่ได้โหลด/ไม่มี */
  const [auditByClaimId, setAuditByClaimId] = useState<Map<string, AuditEntry[]> | undefined>(undefined);

  /**
   * บัญชีสมาชิกที่โหลดรายละเอียดมาแล้ว — กันยิงซ้ำเมื่อเปิดคำขอของคนเดิม
   * เก็บเป็น ref ไม่ใช่ state เพราะไม่ต้องให้ re-render ตอนเซ็ต
   */
  const loadedAccounts = useRef<Set<number>>(new Set());

  const load = useCallback(() => {
    let cancelled = false;
    setState('loading');

    const work = isMock
      ? mock!.listClaims({ pageSize: 2000 }).then((page) => page.results.map(mock!.toUiClaim as never) as UiClaim[])
      : Promise.all([
          api.listClaims({ limit: PAGE_LIMIT, ordering: 'created', ...serverFilter }),
          api.listEarningPolicies(),
          /**
           * ประวัติการตรวจ — ดึงมาทั้ง division แล้วจัดกลุ่มในเครื่อง
           *
           * ⚠️ endpoint นี้กรองตามคำขอไม่ได้ (ไม่รับพารามิเตอร์กรอง) จึงไม่มีทางอื่น
           *    ถ้าล้มเหลวไม่ควรทำให้ทั้งคิวพัง — ประวัติเป็นของเสริม ไม่ใช่ของหลัก
           */
          api.listClaimActionLogs().catch(() => null),
        ]).then(([page, policies, logs]) => {
          const active = policies.items.find((p) => p.default) ?? policies.items[0];
          setRewardMultiplier(active ? Number(active.rewardMultiplier) : null);
          setAuditByClaimId(logs ? toAuditEntriesByClaim(logs.items) : undefined);
          return page.items.map(crmToUi);
        });

    work
      .then((rows) => { if (!cancelled) { setClaims(rows); setState('ready'); } })
      .catch(() => { if (!cancelled) setState('error'); });

    return () => { cancelled = true; };
  }, [serverFilter]);

  useEffect(() => load(), [load, refreshKey]);

  /**
   * รับเงื่อนไขจากหน้าจอมากรองฝั่ง server
   *
   * ⚠️ **backend ค้นได้เฉพาะชื่อคน** ไม่รู้จักรหัสคำขอหรือเลขคำสั่งซื้อ
   *    ถ้าส่งข้อความที่ดูเป็นรหัสไป จะได้ผลลัพธ์ว่างทั้งที่ของมีอยู่
   *    จึงส่งไปเฉพาะข้อความที่**ไม่ใช่**รูปแบบรหัส แล้วปล่อยให้หน้าจอกรองรหัสเองในเครื่อง
   *
   *    ตราบใดที่คำขอทั้ง division ยังไม่เกิน PAGE_LIMIT การกรองในเครื่องก็ครบอยู่แล้ว
   *    ประโยชน์จริงของการกรองฝั่ง server คือตอนข้อมูลโตเกินหน้าเดียว
   */
  const onServerFilter = useCallback(
    (f: { query: string; amountMin: string; amountMax: string }) => {
      if (isMock) return;
      const q = f.query.trim();
      const looksLikeCode = /^(CLM-?)?\d+$/i.test(q) || /^[A-Z]{2,}-/i.test(q);
      const next: { name?: string; amountGte?: number; amountLte?: number } = {};
      if (q && !looksLikeCode) next.name = q;
      if (f.amountMin) next.amountGte = Number(f.amountMin);
      if (f.amountMax) next.amountLte = Number(f.amountMax);
      setServerFilter((prev) =>
        prev.name === next.name && prev.amountGte === next.amountGte && prev.amountLte === next.amountLte
          ? prev
          : next);
    },
    [],
  );

  const onDecide = useCallback(
    async (claimId: string, next: QueueStatus, extra: { amount?: number; reasonCode?: string; reasonLabel?: string; detail?: string; comment?: string }) => {
      if (isMock) {
        if (next === 'approved') await mock!.approveClaim(claimId, { verifiedAmount: extra.amount ?? null });
        else await mock!.rejectClaim(claimId, {
          verifiedAmount: extra.amount ?? null,
          rejectReasonCode: extra.reasonCode,
          rejectReason: extra.reasonLabel,
        });
        return;
      }

      /**
       * ⚠️ **ต้องส่ง `amount` ไปด้วยตอนอนุมัติเสมอ** ไม่งั้นได้ 400 "Amount is required"
       *
       *    ใช้ยอดที่เจ้าหน้าที่แก้ถ้ามี ไม่งั้นใช้ยอดเดิมของคำขอ
       *    การเขียนทับ `amount` แบบนี้คือวิธีที่ CRM รองรับการแก้ยอดก่อนอนุมัติ (FR-ADM-12)
       *
       *    ข้อจำกัดที่เหลือ: ยอดเดิมที่ลูกค้ากรอกจะถูกทับหายไป ตรวจย้อนหลังไม่ได้ว่าแก้จากเท่าไร
       */
      const id = Number(claimId);
      const current = claims?.find((c) => c.id === claimId);
      const amount = extra.amount ?? current?.verifiedAmount ?? current?.declaredAmount;

      if (next === 'approved') {
        /* comment บังคับเมื่อยอดต่างจากที่ลูกค้ากรอก — หน้าจอกันไว้แล้ว แต่ backend ตรวจซ้ำ */
        await api.actOnClaim(id, CRM_ACTION.APPROVE, { amount, comment: extra.comment || undefined });
      } else {
        await api.actOnClaim(id, CRM_ACTION.REJECT, {
          amount,
          /* รายละเอียดที่เจ้าหน้าที่พิมพ์ต้องไปด้วย — เหตุผลบางข้อบังคับกรอก และเขียนไว้ให้ลูกค้าอ่าน
             backend มีช่องเดียว จึงรวมเป็นข้อความเดียว */
          rejectedReason: rejectReasonText(extra.reasonCode, extra.reasonLabel, extra.detail),
        });
      }
    },
    [claims],
  );

  /** ปุ่ม "เลิกทำ" — CRM รองรับผ่าน action ROLLBACK ตรง ๆ */
  const onUndo = useCallback(async (claimId: string) => {
    if (isMock) { await mock!.undoDecision(claimId); return; }
    /* backend บังคับเหตุผลตอนย้อนสถานะ — ปุ่มนี้อยู่ใน toast ไม่กี่วินาทีหลังตัดสิน จึงเป็นการแก้กดผิดเสมอ */
    await api.actOnClaim(Number(claimId), CRM_ACTION.ROLLBACK, { comment: UNDO_COMMENT });
  }, []);

  /**
   * เปิดคำขอ → โหลดข้อมูลสมาชิกของรายการนั้นเพิ่ม
   *
   * ⚠️ ไม่ยิงล่วงหน้าทุกแถว — คิวมีได้ถึง 500 รายการต่อรอบ การยิงล่วงหน้า
   *    คือยิงหลายร้อยครั้งเพื่อข้อมูลที่เจ้าหน้าที่เปิดดูจริงไม่กี่รายการ
   *
   * ล้มเหลวแล้วเงียบโดยตั้งใจ — ระดับสมาชิกเป็นบริบทประกอบการตัดสิน
   * ไม่ใช่ของที่ขาดไม่ได้ ถ้าโหลดไม่ได้ก็ยังตัดสินจากยอดและสลิปได้
   */
  const onOpenClaim = useCallback(
    (claim: UiClaim) => {
      if (isMock) return;

      const accountId = Number(claim.member.memberCode.replace(/\D/g, ''));
      if (!Number.isFinite(accountId) || accountId === 0) return;
      if (loadedAccounts.current.has(accountId)) return;
      loadedAccounts.current.add(accountId);

      void api
        .getAccount(accountId)
        .then((acc) => {
          setClaims((prev) =>
            prev?.map((c) =>
              c.member.memberCode === claim.member.memberCode
                ? { ...c, member: mergeAccountIntoMember(c.member, acc) }
                : c,
            ),
          );
        })
        .catch(() => {
          /* ให้ลองใหม่ได้ครั้งหน้า */
          loadedAccounts.current.delete(accountId);
        });
    },
    [api, isMock],
  );

  return (
    <ClaimQueueScreen
      onOpenClaim={onOpenClaim}
      initialState={state}
      claims={claims ?? []}
      onDecide={onDecide}
      onUndo={onUndo}
      /**
       * ⚠️ ค่าเริ่มต้นของตัวกรองผู้รับผิดชอบต้องเป็น "ทุกคน" เมื่อต่อ CRM
       *
       *    หน้าจอตั้งค่าเริ่มต้นเป็น "ของฉัน" ซึ่งถูกต้องกับข้อมูลจำลองที่มี assignee
       *    แต่ CRM **ไม่มีฟิลด์ผู้รับผิดชอบเลย** adapter จึงใส่ null ทุกแถว
       *    ผลคือตัวกรองเริ่มต้นคัดออกหมด เห็นคิวว่างทั้งที่มีคำขออยู่จริง
       *    (เจอจริงตอนต่อ CRM ครั้งแรก — คิวขึ้น 0 รายการทั้งที่ API ส่งมา 1 รายการ)
       */
      initialAssignee={isMock ? undefined : 'all'}
      onRefresh={load}
      refreshing={state === 'loading'}
      onServerFilter={isMock ? undefined : onServerFilter}
      auditByClaimId={isMock ? undefined : auditByClaimId}
      /**
       * ความสามารถของแหล่งข้อมูล — ปิดสิ่งที่ CRM ไม่มี
       * เพื่อไม่ให้หน้าจอโชว์คอลัมน์และตัวกรองที่ว่างเปล่าทุกแถว
       */
      capabilities={isMock ? undefined : {
        /* ✅ ระดับสมาชิกและคะแนนคงเหลือโหลดเพิ่มตอนเปิดดูรายการ (onOpenClaim)
              ⛔ ส่วนสถิติการส่งคำขอ (ส่งมากี่ครั้ง ผ่านกี่ครั้ง) backend ยังไม่มี
                 หน้าจอซ่อนกล่องนั้นเองเมื่อค่าเป็น 0 */
        memberStats: true,
        assignee: false,    // CRM ไม่มีฟิลด์ผู้รับผิดชอบ
        /* ✅ มีชนิดเดียวคือยอดที่ OCR อ่านได้ไม่ตรงกับที่ลูกค้ากรอก
              ⛔ สลิปซ้ำ/ส่งถี่/ยอดผิดปกติ ยังไม่มีข้อมูลจาก backend (ดู anomaliesOf) */
        anomalies: true,
        source: false,      // ไม่มีระบบตรวจอัตโนมัติ ทุกคำขอต้องใช้คนตรวจ
        auditTrail: true,   // ✅ point-claim-action-log/ ใช้ได้แล้ว
        bulkApprove: true,  // ไม่มี endpoint กลุ่ม แต่ยิงทีละรายการได้
      }}
      pointsForAmount={
        isMock || rewardMultiplier === null
          ? undefined
          : (amount) => Math.floor(amount * rewardMultiplier)
      }
    />
  );
}
